import React, { useState, useEffect, useCallback } from 'react';
import { Box, Button, IconButton, CircularProgress, Stack } from '@mui/material';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import DeleteIcon from '@mui/icons-material/Delete';
import { classes } from '../styles';
import { ILocationBasedAS, ILocationBasedProps } from '../../types';
import FormSelectField from '../../../Components/CustomUIElements/FormSelectField';
import NoAutoStatusSVG from './NoAutoStatusSVG';
import Switch from '@mui/material/Switch';
import { useQuery } from '@tanstack/react-query';
import Api from '../../../Application/API';
import { ICountries } from '@/components/NewUI/Components/sharedTypes';

export default function LocationBased({
  jobId,
  setSnackbar,
  autoStatuses,
  setAutoStatuses,
  applicationStatuses
}: ILocationBasedProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [lastAction, setLastAction] = useState<{
    action: 'add' | 'update' | 'delete' | null;
    id: null | number;
  }>({ action: null, id: null });

  const handleAddNewAutoStatus = async () => {
    setIsLoading(true);
    try {
      setLastAction({ action: 'add', id: null });
      const response = await fetch(`/api/v4/jobs/${jobId}/auto_status_location_based`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          auto_status_location_based: {
            status_id: applicationStatuses[0].id,
            enabled: false,
            auto_status_condition_attributes: {
              country_id: null,
              state_id: null,
              is_negated: false
            }
          }
        })
      });
      if (response.ok) {
        const data = await response.json();
        setAutoStatuses([...autoStatuses, data]);
      } else {
        throw new Error('Error adding auto status');
      }
    } catch (error: unknown) {
      setSnackbar({
        message: error instanceof Error ? error.message : 'Error creating auto status',
        state: 'error'
      });
    } finally {
      setIsLoading(false);
      setLastAction({ action: null, id: null });
    }
  };

  const handleDeleteAutoStatus = async (index: number) => {
    setAutoStatuses((prev: ILocationBasedAS[]) => prev.filter((_, i) => i !== index));
    setLastAction({ action: 'delete', id: autoStatuses[index].id });
  };

  const handleChangeStatus = async (index: number, statusId: number) => {
    setAutoStatuses((prev: ILocationBasedAS[]) => {
      if (index < 0 || index >= prev.length) return prev;

      return prev.map((item, i) =>
        i === index ? { ...item, status: { ...prev[index].status, id: statusId } } : item
      );
    });
    setLastAction({ action: 'update', id: autoStatuses[index].id });
  };

  const handleUpdate = async (
    index: number,
    value: string | number,
    field: 'country_id' | 'state_id' | 'is_negated'
  ) => {
    setAutoStatuses((prev: ILocationBasedAS[]) => {
      if (index < 0 || index >= prev.length) return prev;

      return prev.map((item, i) =>
        i === index
          ? {
              ...item,
              condition: {
                ...prev[index].condition,
                [field]: value === '' ? null : Number(value)
              }
            }
          : item
      );
    });
    setLastAction({ action: 'update', id: autoStatuses[index].id });
  };

  const handleToggleEnable = async (index: number) => {
    setAutoStatuses((prev: ILocationBasedAS[]) => {
      if (index < 0 || index >= prev.length) return prev;

      return prev.map((item, i) =>
        i === index ? { ...item, enabled: !prev[index].enabled } : item
      );
    });
    setLastAction({ action: 'update', id: autoStatuses[index].id });
  };

  const updateApi = useCallback(
    async (id: number) => {
      const autoStatus = autoStatuses.find((i) => i.id === id);
      if (!autoStatus) throw new Error('Auto status not found');
      setIsLoading(true);
      const payload = {
        auto_status_location_based: {
          status_id: autoStatus.status.id,
          enabled: autoStatus.enabled,
          auto_status_condition_attributes: autoStatus.condition
        }
      };
      const response = await fetch(
        `/api/v4/jobs/${jobId}/auto_status_location_based/${autoStatus.id}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        }
      );
      if (!response.ok) {
        throw new Error('Error updating auto status');
      }
    },
    [autoStatuses, jobId]
  );

  const deleteApi = useCallback(
    async (id: number) => {
      const response = await fetch(`/api/v4/jobs/${jobId}/auto_status_location_based/${id}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' }
      });
      if (!response.ok) {
        throw new Error('Error deleting auto status');
      }
    },
    [jobId]
  );

  useEffect(() => {
    if (lastAction.id === null || lastAction.action === null || !autoStatuses) return;
    const performAction = async () => {
      try {
        if (lastAction.action == 'update' && lastAction.id) await updateApi(lastAction.id);
        if (lastAction.action == 'delete' && lastAction.id) await deleteApi(lastAction.id);
      } catch (error) {
        setSnackbar({
          message: 'There was an error saving your changes',
          state: 'error'
        });
      } finally {
        setIsLoading(false);
        setLastAction({ action: null, id: null });
      }
    };
    performAction();
  }, [autoStatuses, deleteApi, lastAction, setSnackbar, updateApi]);

  const {
    data: countriesStates,
    error: getCountryApiError,
    isLoading: isLoadingCountriesStates
  } = useQuery({
    queryKey: ['countries states'],
    queryFn: async (): Promise<ICountries[]> => {
      const { res } = await Api.getCountriesStates();
      return res.countries;
    }
  });

  useEffect(() => {
    if (getCountryApiError) {
      setSnackbar({
        message: `There was an error getting countries and states, ${getCountryApiError}`,
        state: 'error'
      });
    }
  }, [getCountryApiError, setSnackbar]);

  return (
    <Box sx={classes.autoStatusesContainer}>
      <Box sx={{ position: 'relative' }}>
        {autoStatuses?.map((autoStatus, statusIndex) => (
          <Box sx={classes.autoStatusesSubContainer} key={autoStatus.id}>
            <Box sx={classes.statusFormLine}>
              <Box component={'h4'}>If candidate is</Box>
              <FormSelectField
                label="Negated"
                options={[
                  { value: '0', label: 'in' },
                  { value: '1', label: 'not in' }
                ]}
                defaultValue={autoStatus.condition.is_negated ? '1' : '0'}
                onChange={(value) => handleUpdate(statusIndex, value, 'is_negated')}
                styles={{ width: '90px' }}
                isDisabled={isLoading || isLoadingCountriesStates}
              />
              <Box sx={{ flex: 1, '& > *': { width: '100%' } }}>
                <FormSelectField
                  label="Country"
                  defaultValue={autoStatus.condition.country_id?.toString() ?? undefined}
                  options={(countriesStates || []).map((country: ICountries) => ({
                    value: country.id.toString(),
                    label: country.printable_name
                  }))}
                  onChange={(value) => handleUpdate(statusIndex, value, 'country_id')}
                  styles={{ width: '100%' }}
                  isDisabled={isLoading || isLoadingCountriesStates}
                  placeholder="Country"
                  clearable={true}
                />
              </Box>
              <Box sx={{ flex: 1, '& > *': { width: '100%' } }}>
                <FormSelectField
                  label="State"
                  options={(countriesStates || [])
                    .filter((country) => country.id === autoStatus.condition.country_id)[0]
                    ?.states?.map((state) => ({
                      value: state.id.toString(),
                      label: state.name
                    }))}
                  defaultValue={autoStatus.condition.state_id?.toString() ?? undefined}
                  onChange={(value) => handleUpdate(statusIndex, value, 'state_id')}
                  styles={{ width: '100%' }}
                  isDisabled={isLoading || isLoadingCountriesStates}
                  placeholder="State (optional)"
                  clearable={true}
                />
              </Box>
            </Box>
            <Box sx={classes.statusFormLine}>
              <Box component={'h4'}>then</Box>
              <FormSelectField
                label="Status"
                options={
                  applicationStatuses
                    ? applicationStatuses?.map((status) => ({
                        value: status.id.toString(),
                        label: status.name
                      }))
                    : []
                }
                defaultValue={autoStatus.status.id ? autoStatus.status.id.toString() : ''}
                onChange={(value) => handleChangeStatus(statusIndex, Number(value))}
                styles={{ width: '290px' }}
                isDisabled={isLoading || isLoadingCountriesStates}
              />
              <Box
                sx={{
                  ...classes.switchContainer,
                  fontWeight: autoStatus.enabled ? 'bold' : ''
                }}
              >
                <Box sx={{ marginTop: '12px' }}>
                  <Switch
                    sx={autoStatus.enabled ? classes.switchActive : classes.switch}
                    checked={autoStatus.enabled}
                    size="small"
                    onChange={() => handleToggleEnable(statusIndex)}
                    disabled={isLoading || isLoadingCountriesStates}
                  />
                  <Stack sx={{ marginTop: 1, fontSize: '12px' }}>Enabled</Stack>
                </Box>
              </Box>
              <IconButton
                data-testid="delete-auto-status"
                sx={{ ...classes.deleteButton, position: 'inline', top: '0px' }}
                onClick={() => handleDeleteAutoStatus(statusIndex)}
                disabled={isLoading || isLoadingCountriesStates}
              >
                {isLoading && lastAction.action === 'delete' && lastAction.id === autoStatus.id ? (
                  <CircularProgress size={21} />
                ) : (
                  <DeleteIcon />
                )}
              </IconButton>
            </Box>
          </Box>
        ))}
        {autoStatuses && autoStatuses.length > 0 ? (
          <IconButton
            sx={classes.addNewStatusButton}
            onClick={handleAddNewAutoStatus}
            disabled={isLoading || isLoadingCountriesStates}
          >
            {isLoading && lastAction.action === 'add' ? (
              <CircularProgress size={21} />
            ) : (
              <AddCircleIcon />
            )}
          </IconButton>
        ) : (
          <Box sx={{ ...classes.noQuestionsContainer, margin: '100px 0px' }}>
            <Box>
              <NoAutoStatusSVG />
            </Box>
            <Box sx={classes.noQuestionsText}>You have no auto statuses.</Box>
            <Button
              data-testid="add-new-status-button"
              startIcon={
                isLoading && lastAction.action === 'add' ? (
                  <CircularProgress size={26} />
                ) : (
                  <AddCircleIcon />
                )
              }
              onClick={handleAddNewAutoStatus}
              sx={classes.noStatusesAddButton}
              disabled={isLoading || isLoadingCountriesStates}
            >
              Add new status
            </Button>
          </Box>
        )}
      </Box>
    </Box>
  );
}
